import appStore from '../utils/state';
import gsap from 'gsap';
import type { CartState } from '../types';
import { initFreeGiftSync, refreshCartFromServer } from '../utils/syncFreeGift';

/**
 * The cart drawer shell: owns its own open/close and empty/form state
 * transitions, and the cart item count label. It delegates rendering of the
 * actual line-items list to `cart-items.ts` — this component never touches
 * `<cart-item>` elements directly.
 */
export default class CartDrawer extends HTMLElement {
  private quantity: number = 0;
  private isEmpty: boolean = false;
  private cartCountLabel: string | null = null;

  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;
  private goToFormTl: gsap.core.Timeline | null = null;
  private goToEmptyTl: gsap.core.Timeline | null = null;

  private dialog: HTMLDialogElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private cartCount: HTMLParagraphElement | null = null;
  private items: HTMLDivElement | null = null;
  private empty: HTMLDivElement | null = null;
  private checkout: HTMLDivElement | null = null;

  private triggerElement: HTMLElement | null = null;

  private unsubscribeFromCartModalState: (() => void) | null = null;
  private unsubscribeFromCartUpdating: (() => void) | null = null;
  private unsubscribeFromCartUpdated: (() => void) | null = null;

  private onCartModalToggleBound = this.onCartModalToggle.bind(this);
  private onCartUpdatingBound = this.onCartUpdating.bind(this);
  private onCloseButtonClickBound = this.onCloseButtonClick.bind(this);
  private onCartUpdatedBound = this.onCartUpdated.bind(this);
  private onPageShowBound = this.onPageShow.bind(this);
  private onDialogCancelBound = this.onDialogCancel.bind(this);
  private onDialogCloseBound = this.onDialogClose.bind(this);
  private onDialogClickBound = this.onDialogClick.bind(this);

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.cartCountLabel = this.dataset.cartCountLabel ?? null;
    this.quantity = parseInt(this.dataset.quantity ?? '0', 0);
    this.isEmpty = this.quantity === 0;

    this.dialog = this.querySelector('dialog');
    this.closeButton = this.querySelector('[data-cart-modal-ref="close-button"]');
    this.cartCount = this.querySelector('[data-cart-drawer-ref="cart-count"]');
    this.items = this.querySelector('[data-cart-drawer-ref="items"]');
    this.empty = this.querySelector('[data-cart-drawer-ref="empty"]');
    this.checkout = this.querySelector('[data-cart-drawer-ref="checkout"]');

    this.addEventListeners();
    this.subscribe();
  }

  addEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.addEventListener('cancel', this.onDialogCancelBound);
      this.dialog.addEventListener('close', this.onDialogCloseBound);
      this.dialog.addEventListener('click', this.onDialogClickBound);
    }

    document.addEventListener('DOMContentLoaded', () => {
      void initFreeGiftSync();
    });

    // Back from checkout often restores a stale DOM (bfcache or back_forward).
    // Liquid is not re-run — refresh cart sections from the server instead.
    window.addEventListener('pageshow', this.onPageShowBound);
  }

  removeEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.removeEventListener('cancel', this.onDialogCancelBound);
      this.dialog.removeEventListener('close', this.onDialogCloseBound);
      this.dialog.removeEventListener('click', this.onDialogClickBound);
    }
    window.removeEventListener('pageshow', this.onPageShowBound);
  }

  private onPageShow(event: PageTransitionEvent): void {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const isBackForward = event.persisted || navigation?.type === 'back_forward';

    if (!isBackForward) return;

    void refreshCartFromServer();
  }

  subscribe(): void {
    this.unsubscribeFromCartModalState = appStore.subscribeKey('isCartModalOpen', this.onCartModalToggleBound);
    this.unsubscribeFromCartUpdating = appStore.subscribeKey('cartIsUpdating', this.onCartUpdatingBound);
    this.unsubscribeFromCartUpdated = appStore.subscribeKey('cart', this.onCartUpdatedBound);
  }

  unsubscribe(): void {
    if (this.unsubscribeFromCartModalState) {
      this.unsubscribeFromCartModalState();
    }
    if (this.unsubscribeFromCartUpdating) {
      this.unsubscribeFromCartUpdating();
    }
    if (this.unsubscribeFromCartUpdated) {
      this.unsubscribeFromCartUpdated();
    }
  }

  onCloseButtonClick(): void {
    appStore.actions.closeCartModal();
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    appStore.actions.closeCartModal();
  }

  onDialogClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      appStore.actions.closeCartModal();
    }
  }

  onDialogClose(): void {
    if (appStore.getState().isCartModalOpen) {
      appStore.actions.closeCartModal();
    }

    this.restoreFocus();
  }

  captureTrigger(): void {
    const trigger = document.querySelector('[aria-controls="cart-drawer-dialog"]');

    if (trigger instanceof HTMLElement) {
      this.triggerElement = trigger;
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      this.triggerElement = activeElement;
    }
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  onCartUpdating(cartIsUpdating: boolean): void {
    if (this.cartCount) {
      this.cartCount.classList.toggle('shimmer-text', cartIsUpdating);
    }
  }

  onCartUpdated(cart: CartState): void {
    const wasEmpty = this.isEmpty;
    this.isEmpty = cart.data?.item_count === 0;

    if (this.cartCount && this.cartCountLabel) {
      this.cartCount.textContent = this.cartCountLabel.replace('[count]', cart.data?.item_count?.toString() ?? '0');
    }

    // Only trigger the empty/form transition animation when crossing the
    // boundary (0 <-> non-0 items), not on every cart update — otherwise a
    // simple quantity bump would needlessly replay the state-switch timeline.
    if (wasEmpty !== this.isEmpty) {
      if (this.isEmpty) {
        this.goToEmptyState();
      } else {
        this.goToFormState();
      }
    }
  }

  onCartModalToggle(isCartModalOpen: boolean): void {
    if (isCartModalOpen) {
      this.show();
      // Re-fetch cart + sections so discounts / free-gift state match the server
      // (e.g. after customer login without a full page reload).
      void refreshCartFromServer();
    } else {
      this.hide();
    }
  }

  /** Reveals the items list/checkout area and hides the empty-state block. */
  goToFormState(): void {
    if (this.goToFormTl) {
      this.goToFormTl.kill();
    }
    this.goToFormTl = gsap.timeline();
    this.goToFormTl.set(this.empty, { autoAlpha: 0 }, 0);
    this.goToFormTl.set(this.items, { autoAlpha: 1 }, 0);
    this.goToFormTl.set(this.checkout, { height: 'auto' }, 0);
  }

  /** Collapses the checkout area, hides the items list, and reveals the empty-state block. */
  goToEmptyState(): void {
    if (this.goToEmptyTl) {
      this.goToEmptyTl.kill();
    }

    this.goToEmptyTl = gsap.timeline();
    this.goToEmptyTl.to(this.checkout, { height: 0, duration: 0.7, willChange: 'height', ease: 'expo.inOut' }, 0);
    this.goToEmptyTl.set(this.items, { autoAlpha: 0 }, 0.7);
    this.goToEmptyTl.to(this.empty, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0.1);
  }

  /** Fades in and slides the whole drawer into view. */
  show(): void {
    if (!this.dialog) {
      return;
    }

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    gsap.set(this.dialog, { autoAlpha: 0, visibility: 'visible' });

    this.captureTrigger();

    if (!this.dialog.open) {
      this.dialog.showModal();
    }

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this.dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this.dialog, { x: '5%' }, { x: 0, force3D: true, duration: 0.7, ease: 'expo.out' }, 0);
  }

  /** Fades out and slides the whole drawer out of view. */
  hide(): void {
    if (!this.dialog) {
      return;
    }

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline({
      onComplete: () => {
        if (this.dialog?.open) {
          this.dialog.close();
        }
      },
    });

    this.hideTl.to(this.dialog, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
    this.hideTl.to(this.dialog, { x: '5%', duration: 0.3, force3D: true, ease: 'expo.out' }, 0);
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();
  }
}

customElements.define('cart-drawer', CartDrawer);
